import { format, toDate } from 'date-fns-tz';
import type { StringSchema } from 'yup';
import { string } from 'yup';
import type { Nilable, Nullable } from '~/utilities/type-guards';
import {
  isEmptyString,
  isNil,
  isNonEmptyRecord,
  isNotNil,
  isString,
} from '~/utilities/type-guards';
import { includesString, parseLocalStorageData, setLocalStorageData } from '~/utilities/util';
import {
  AUDIENCE_MEMBER_ID_LOCAL_STORAGE_KEY,
  LIVE_DATA_LOCAL_STORAGE_KEY,
  PARTICIPANT_TYPES,
  PLACEHOLDER_IMAGE_URL,
  REGISTRATION_LOCAL_STORAGE_KEY,
  DEFAULT_THUMBNAIL,
  DEFAULT_THUMBNAIL_2,
  LIFECYCLE_STATUS,
  HMS_ROLE,
  EXTERNAL_DEPENDENCY_STATUS,
  ANONYMOUS_USER_USERNAME,
} from '~/singlePageApp/screens/LiveStreamScreen/shared/constants';
import type { UserType } from '~/singlePageApp/screens/LiveStreamScreen/Broadcast/types/UserType';
import { legacyUserTypeSchema } from '~/singlePageApp/screens/LiveStreamScreen/Broadcast/types/UserType';
import type {
  LifecycleStatus,
  ExternalDependencies,
} from '~/singlePageApp/screens/LiveStreamScreen/Broadcast/types/LiveStreamEvent';
import type {
  LiveDataForAllEvents,
  LegacyAudienceDataForAllEvents,
} from '~/singlePageApp/screens/LiveStreamScreen/Broadcast/types/LiveDataForAllEvents';
import type { AllUserData } from '~/singlePageApp/screens/LiveStreamScreen/Broadcast/types/UserData';
import {
  userEmailSchema,
  commonUserDataSchema,
} from '~/singlePageApp/screens/LiveStreamScreen/Broadcast/types/UserData';
import { v4 as uuidv4 } from 'uuid';
import type { ParticipantType } from '~/singlePageApp/screens/LiveStreamScreen/Broadcast/types/ParticipantType';
import { z } from 'zod';
import sample from 'lodash/sample';
import { match } from 'ts-pattern';
/**
 * Return a human readable time the event will start
 *
 * Takes the form of "Scheduled for Feb 4th at 1:36 PM CST"
 *
 * @param scheduledFor - The date and time (in ISO format) that the event has been scheduled to start at
 */
export const toScheduledForString = (scheduledFor: string): string => {
  const date = toDate(scheduledFor);
  return `Scheduled for ${format(date, 'MMM do')} at ${format(date, 'p zzz')}`;
};

/**
 * Schema to validate the "display name" of a user within the live app
 *
 * @param nameLabel - The string the error message will display
 */
export const nameSchema = (nameLabel: string): StringSchema => {
  const maxCharacterCount = 54;

  return string().label(nameLabel).trim().required().max(maxCharacterCount);
};

/**
 * Function to return undefined in place of the zebra stripe lines thumbnail
 *
 * @param url - The thumbnail url returned from the server
 */
export const thumbnailUrlWithoutZebraStripes = (url: Nilable<string>): Nilable<string> =>
  // We want a different placeholder image than the zebra lines
  // which is returned from the server as the default.
  url === '/assets/images/zebra/elements/dashed-thumbnail.png' ? undefined : url;

/**
 * Function to return thumbnailPlaceholderImageUrl.
 *
 * @param IsBrandRefreshEnabled - The thumbnail url returned from the server
 */
export const generateThumbnailPlaceholder = (brandRefreshEnabled: boolean): string => {
  return brandRefreshEnabled
    ? sample([DEFAULT_THUMBNAIL, DEFAULT_THUMBNAIL_2])
    : PLACEHOLDER_IMAGE_URL;
};

/**
 * Whether the current user is the host or not
 *
 * @param userType - The type of user
 */
export const isUserHost = (userType: UserType): boolean =>
  includesString([HMS_ROLE.Host, HMS_ROLE.Host720p], userType);

/**
 * Whether the current user is a participant of the event or not
 *
 * @param userType - The type of user
 */
export const isUserParticipant = (userType: string): userType is ParticipantType =>
  includesString(PARTICIPANT_TYPES, userType);

/**
 * Participants will always see the side panel, and audience members will
 * only see it on large devices
 *
 * @param userType - The type of user
 */
export const shouldShowSidePanel = (userType: UserType): boolean => {
  const mediumScreenWidth = 768;
  const isBiggerDevice = document.body.clientWidth >= mediumScreenWidth;

  return isUserParticipant(userType) || isBiggerDevice;
};

/**
 * This function returns the state the user is in when joining the chat
 *
 * The name should only ever be nil if the current user is
 * an audience member since a host and panelist user should
 * already have their name saved in state - Burns
 *
 * Without a username present, the user is presumed "joining". If the event
 * is live and/or its not an audience member, this will return "active". Future logic,
 * will potential determine when we change the state to "waiting". - Josh
 *
 * @param username - The current audience member's username
 */
export const getUserJoinState = (username: Nilable<string>): 'active' | 'joining' =>
  isNil(username) || isEmptyString(username) || username === ANONYMOUS_USER_USERNAME
    ? 'joining'
    : 'active';

/**
 * This function returns whether the live stream event is ready to broadcast
 *
 * @param lifecycleStatus - The lifecycle status of the event
 * @returns Whether the live stream event is ready to broadcast
 */
export const isEventReady = (lifecycleStatus: LifecycleStatus): boolean =>
  includesString([LIFECYCLE_STATUS.Pending, LIFECYCLE_STATUS.Ready], lifecycleStatus);

/**
 * This function returns whether the live stream event has started
 *
 * @param lifecycleStatus - The lifecycle status of the event
 * @returns Whether the live stream event has started
 */
export const isEventLive = (lifecycleStatus: LifecycleStatus): boolean =>
  lifecycleStatus === LIFECYCLE_STATUS.Started;

export const isEventOnAir = (lifecycleStatus?: LifecycleStatus): boolean =>
  lifecycleStatus === LIFECYCLE_STATUS.Live;

/**
 * This function returns whether the live stream event has ended
 *
 * @param lifecycleStatus - The lifecycle status of the event
 * @returns Whether the live stream event has ended
 */
export const isEventEnded = (lifecycleStatus: LifecycleStatus): boolean =>
  includesString([LIFECYCLE_STATUS.Ended, LIFECYCLE_STATUS.Vodready], lifecycleStatus);

export const isEventRoomDisabled = (externalDependencies: ExternalDependencies): boolean =>
  externalDependencies.eventRoom === EXTERNAL_DEPENDENCY_STATUS.Disabled;

/**
 * This function returns either the event thumbnail or a placeholder thumbnail.
 * Returning null will fallback to the default gradient thumbnail.
 *
 * @param thumbnailUrl - The thumbnail url returned from the server
 * @returns The event thumbnail or a placeholder thumbnail
 */
export const getEventThumbnail = (thumbnailUrl: Nilable<string>): Nilable<string> =>
  thumbnailUrlWithoutZebraStripes(thumbnailUrl ?? undefined);

/**
 * Save the user data for a specific event to "live data" in local storage
 *
 * @param liveData - The live data for all events
 * @param hashedId - The hashed id of the event
 * @param userData - The user data to be saved
 */
export const setUserDataInLiveData = (
  liveData: LiveDataForAllEvents,
  hashedId: string,
  { id, role, username, email }: AllUserData,
): void => {
  const newUserDataForAllEvents: LiveDataForAllEvents = {
    common: {
      ...liveData.common,
      user: { ...liveData.common.user, [role]: { id, username } },
    },
    events: {
      ...liveData.events,
      [hashedId]: {
        ...(liveData.events[hashedId] ?? {}),
        user: {
          host: null,
          panelist: null,
          audience: null,
          ...(liveData.events[hashedId]?.user ?? {}),
          [role]: {
            email,
          },
        },
      },
    },
  };

  setLocalStorageData(LIVE_DATA_LOCAL_STORAGE_KEY, newUserDataForAllEvents);
};

/**
 * Save the user data for a specific event as well as all audience data for
 * the current user for past events to "live data" in local storage
 *
 * @param legacyAudienceDataForAllEvents - The audience data for all past events
 * @param hashedId - The hashed id of the event
 * @param userData - The user data to be saved
 */
export const setUserDataFromLegacyAudienceData = (
  legacyAudienceDataForAllEvents: LegacyAudienceDataForAllEvents,
  hashedId: string,
  { id, role, username, email }: AllUserData,
): void => {
  const newUserDataForAllEvents = legacyAudienceDataForAllEvents.reduce<LiveDataForAllEvents>(
    (acc, userDataForEvent) => {
      const newRole =
        isNil(userDataForEvent.role) || userDataForEvent.role === 'audienceMember'
          ? 'audience'
          : userDataForEvent.role;

      return hashedId !== userDataForEvent.eventId
        ? {
            ...acc,
            events: {
              ...acc.events,
              [userDataForEvent.eventId]: {
                user: {
                  ...(acc.events[userDataForEvent.eventId]?.user ?? {
                    host: null,
                    panelist: null,
                    audience: null,
                  }),
                  [newRole]: {
                    email: userDataForEvent.email,
                  },
                },
              },
            },
          }
        : acc;
    },
    {
      common: { user: { host: null, panelist: null, audience: null, [role]: { id, username } } },
      events: {
        [hashedId]: {
          user: { host: null, panelist: null, audience: null, [role]: { email } },
        },
      },
    },
  );

  setLocalStorageData(LIVE_DATA_LOCAL_STORAGE_KEY, newUserDataForAllEvents);
};

/**
 * Get the user data for all events from local storage
 *
 * @returns The data the user set up for all events
 */
export const getLegacyAudienceDataForAllEvents = (): LegacyAudienceDataForAllEvents => {
  const defaultValue: LegacyAudienceDataForAllEvents = [];

  const parsedLegacyData = z
    .array(
      z.object({
        eventId: z.string(),
        id: z.string().optional(),
        role: legacyUserTypeSchema.optional(),
        firstName: z.string(),
        lastName: z.string(),
        email: z.string(),
      }),
    )
    .safeParse(parseLocalStorageData(REGISTRATION_LOCAL_STORAGE_KEY, defaultValue));

  return parsedLegacyData.success
    ? parsedLegacyData.data.reduce<LegacyAudienceDataForAllEvents>((acc, unknownData) => {
        return acc.concat({
          eventId: unknownData.eventId,
          firstName: unknownData.firstName,
          lastName: unknownData.lastName,
          email: unknownData.email,
          id: isString(unknownData.id) ? unknownData.id : '',
          role: isString(unknownData.role) ? unknownData.role : 'audience',
        });
      }, [])
    : defaultValue;
};

/**
 * Get the user data for all events from local storage
 *
 * @returns The data the user set up for all events
 */
export const getUserDataForAllEvents = (): LiveDataForAllEvents => {
  const defaultValue: LiveDataForAllEvents = {
    common: { user: { host: null, panelist: null, audience: null } },
    events: {},
  };

  const parsedData = z
    .object({
      common: z.object({
        user: z.object({
          host: commonUserDataSchema,
          panelist: commonUserDataSchema,
          audience: commonUserDataSchema,
        }),
      }),
      events: z.record(
        z.string(),
        z.object({
          user: z.object({
            host: userEmailSchema,
            panelist: userEmailSchema,
            audience: userEmailSchema,
          }),
        }),
      ),
    })
    .safeParse(parseLocalStorageData(LIVE_DATA_LOCAL_STORAGE_KEY, defaultValue));

  return parsedData.success ? parsedData.data : defaultValue;
};

/**
 * Get the user data for a specific event from local storage
 *
 * @param hashedId - The hashed id of the event
 * @returns The data the user set up for a specific event
 */
export const getUserDataForEvent = (hashedId: string, role: UserType): Nullable<AllUserData> => {
  const userDataForAllEvents = getUserDataForAllEvents();

  if (
    isNotNil(userDataForAllEvents.common.user) &&
    isNotNil(userDataForAllEvents.common.user[role])
  ) {
    // We have already checked if the user data exists above, so we can safely
    // use the non-null assertions below - Burns

    const commonUserDataByRole = userDataForAllEvents.common.user[role];
    const userDataForEventByRole = userDataForAllEvents.events[hashedId]?.user[role];

    return {
      id: commonUserDataByRole.id,
      role,
      username: commonUserDataByRole.username,
      email: isNotNil(userDataForEventByRole) ? userDataForEventByRole.email : null,
    };
  }

  if (role === 'audience') {
    const legacyAudienceDataForAllEvents = getLegacyAudienceDataForAllEvents();
    const userDataForEvent = legacyAudienceDataForAllEvents.find((el) => el.eventId === hashedId);

    if (isNotNil(userDataForEvent)) {
      const newUserData = {
        id: userDataForEvent.id,
        role,
        username: `${userDataForEvent.firstName} ${userDataForEvent.lastName}`,
        email: userDataForEvent.email,
      };

      if (isNotNil(userDataForAllEvents.events[hashedId])) {
        setUserDataInLiveData(userDataForAllEvents, hashedId, newUserData);
      } else {
        setUserDataFromLegacyAudienceData(legacyAudienceDataForAllEvents, hashedId, newUserData);
      }

      return newUserData;
    }
  }

  return null;
};

/**
 * Save the user data for a specific event to local storage
 *
 * @param hashedId - The hashed id of the event
 * @param userData - The user data to be saved
 */
export const setUserDataForEvent = (hashedId: string, userData: AllUserData): void => {
  const userDataForAllEvents = getUserDataForAllEvents();

  if (isNonEmptyRecord(userDataForAllEvents)) {
    setUserDataInLiveData(userDataForAllEvents, hashedId, userData);
  } else {
    const legacyAudienceDataForAllEvents = getLegacyAudienceDataForAllEvents();

    setUserDataFromLegacyAudienceData(legacyAudienceDataForAllEvents, hashedId, userData);
  }
};

/**
 * Function that creates audience member id.
 * We use a 32 bit array because we do not need to add any numbers to it.
 * @returns The audience member's id
 */

export const initializeAudienceMemberId = (eventHashedId: string): string => {
  const audienceMemberIdJson = parseLocalStorageData(AUDIENCE_MEMBER_ID_LOCAL_STORAGE_KEY, null);

  const savedHashedId = isNonEmptyRecord(audienceMemberIdJson)
    ? Object.keys(audienceMemberIdJson).find((hashedId) => hashedId === eventHashedId)
    : null;

  const uniqueAudienceMemberId =
    isNotNil(savedHashedId) && isNonEmptyRecord(audienceMemberIdJson)
      ? audienceMemberIdJson[savedHashedId]
      : uuidv4();

  if (isNil(savedHashedId)) {
    setLocalStorageData(AUDIENCE_MEMBER_ID_LOCAL_STORAGE_KEY, {
      [eventHashedId]: uniqueAudienceMemberId,
    });
  }

  return uniqueAudienceMemberId as string;
};

type HMSHLSState = {
  running: boolean;
};

export const isEventBroadcasting = (
  hlsState: HMSHLSState,
  lifecycleStatus?: LifecycleStatus,
): boolean => {
  return (
    (hlsState.running && lifecycleStatus === LIFECYCLE_STATUS.Starting) ||
    lifecycleStatus === LIFECYCLE_STATUS.Started ||
    lifecycleStatus === LIFECYCLE_STATUS.Live
  );
};

type DeviceChangeTuple = [boolean, boolean, boolean];

export const getUserMessage = (
  micChanged: boolean,
  speakerChanged: boolean,
  cameraChanged: boolean,
  selectedAudioInputLabel: string | null,
  selectedAudioOutputLabel: string | null,
  selectedVideoInputLabel: string | null,
): string => {
  return match<DeviceChangeTuple>([micChanged, speakerChanged, cameraChanged])
    .with(
      [true, true, false],
      () => `Your microphone and speaker changed to ${selectedAudioOutputLabel}.`,
    )
    .with([true, false, false], () => `Your microphone changed to ${selectedAudioInputLabel}.`)
    .with([false, true, false], () => `Your speaker changed to ${selectedAudioOutputLabel}.`)
    .with([false, false, true], () => `Your camera changed to ${selectedVideoInputLabel}.`)
    .with(
      [true, true, true],
      () =>
        `Your microphone and speaker changed to ${selectedAudioOutputLabel}. Your camera changed to ${selectedVideoInputLabel}.`,
    )
    .with(
      [true, false, true],
      () =>
        `Your microphone changed to ${selectedAudioInputLabel}. Your camera changed to ${selectedVideoInputLabel}.`,
    )
    .with(
      [false, true, true],
      () =>
        `Your speaker changed to ${selectedAudioOutputLabel}. Your camera changed to ${selectedVideoInputLabel}.`,
    )
    .with([false, false, false], () => '')
    .exhaustive();
};

/**
 * Utility function to sanitize device labels by removing the "Default - " prefix.
 * This prevents unnecessary toast messages when the same device is added twice with
 * the addition of default in the label ie "Default - WH-1000XM5" vs "WH-1000XM5"
 */

export const sanitizeLabel = (label?: string | null): string | null => {
  return label?.replace(/^Default - /, '') ?? null;
};
